import moment from 'moment';
import Divider from '@mui/material/Divider';
import NRLink from '../../../NRLink/NRLink';
import { CONSTANTS } from '../../../../constants/constants';
import { CVE_DETAILS_URL } from '../../../../utils/ApiConstants';
import NRKeyValueDataDisplay from '../../../NRKeyValueDataDisplay/NRKeyValueDataDisplay';
import { useContext, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { VulnDrawerTabsContext } from '../NRVulnerabilityDetails';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import NRReachabilityContainer from '../../../NRReachability/NRReachabilityContainer';
import { useTheme } from '@mui/material/styles';
import NRMetricCard from '../../../NRMetricCard/NRMetricCard';
import NRSBOMIcon from '../../../../NRIcons/NRSBOMIcon';
import NRRemediateIcon from '../../../../NRIcons/NRRemediateIcon';
import { getRemediationIconTextAndColor, getSeverityColor } from '../../../../utils/palette.utils';
import { RemediationDetailsFormatter } from '../../../NRReactGridCustomComponents/NRCustomProviders/RemediationDetailsProvider/RemediationDetailsProvider';
import { capitalizeFirstLetter } from '../../../../utils/utils';
import { useLocation } from 'react-router-dom';
import NRMetricBanner from '../../../NRMetricBanner/NRMetricBanner';
import WithPermissions from '../../../Permissions/WithPermissions';
import RemediationButtonDrawerContainer from '../../VulnerabilitiesTableCustomComponents/RemediationButtonDrawerContainer';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { FiltersContext } from '../../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import NRSBOMDrawerContainer from '../../../../pages/NRSBOMTable/NRSBOMDrawer/NRSBOMDrawerContainer';
import SeverityIconComponent from '../../../../NRIcons/SeverityIconComponent';
import ExploitMaturityIconComponent from '../../../../NRIcons/ExploitMaturityIconComponent';
import NRVulnerabilityJiraTickets from '../NRVulnerabilityJiraTickets/NRVulnerabilityJiraTickets';
import { RBAC_PERMISSIONS } from '../../../../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../../../../pages/NRSettings/NRUsersAndAccess/rbac.constants';

const { DESCRIPTION_SECTION, NVD_STATUS_DATA } = CONSTANTS.TABLE.VULNERABILITIES_TABLE.VULNERABILITY_DETAIL_DRAWER;

const { LINK_TOOLTIP, CVE_SOURCE, DATE, DESCRIPTION, MODIFIED_DATE, PUBLISHED_DATE, NVD_STATUS, REACHABILITY_EVIDENCE } = DESCRIPTION_SECTION;
const { MONTH_DAY_YEAR_SHORT } = CONSTANTS.DATE_FORMAT;
const { LOADING } = CONSTANTS.TABLE.SBOM_TABLE.SBOM_DETAIL_DRAWER;

const NRVulnerabilityDescription = ({ isReachable, vulnerabilityData, cveDetails, isLoading, threshold, scrollTarget, index, scrollableId }) => {
  const { setScrollSpyTabsValue } = useContext(VulnDrawerTabsContext);
  const cveDetailURL = `${CVE_DETAILS_URL}${cveDetails.id}`;
  const {
    name,
    version,
    vendor,
    severity,
    cvssScore,
    cve,
    currentRemediation,
    epssScore,
    maturity,
    id: advisoryId,
    identificationIds
  } = vulnerabilityData;
  const theme = useTheme();
  const location = useLocation();
  const { setOpenDrawer, setDrawerContent, drawerContent, setDrawerTitle, drawerTitle, setHasBack } = useContext(FiltersContext);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold,
    target: !!scrollTarget ? scrollTarget : undefined
  });

  useEffect(() => {
    setScrollSpyTabsValue(index, trigger);
  }, [trigger]);

  const SEVERITY_COLOR = getSeverityColor(severity, theme);
  const [, REMEDIATION_STATUS_LABEL, REMEDIATION_STATUS_COLOR, REMEDIATION_STATUS_COLOR_SECONDARY] = getRemediationIconTextAndColor(
    currentRemediation?.status,
    theme
  );

  function handleOnComponentClick(name, version, vendor) {
    !!drawerContent.length && setHasBack(true);
    setDrawerTitle([...drawerTitle, LOADING]);
    setDrawerContent([...drawerContent, <NRSBOMDrawerContainer searchParams={{ name, version, vendor }} />]);
    setOpenDrawer(true);
  }

  return (
    <>
      <Box padding={theme.spacing(0, 0, 1.5)} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
        <Stack
          direction="row"
          spacing={2}
          mb={theme.spacing(2)}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <NRMetricCard
            minWidth="20%"
            metric={'Severity'}
            result={cvssScore != null && !isNaN(Number(cvssScore)) ? Number(cvssScore).toFixed(1) : ''}
            icon={<SeverityIconComponent severity={severity} />}
            caption={capitalizeFirstLetter(severity)}
          />
          <NRMetricCard
            minWidth="20%"
            metric={'EPSS'}
            result={(epssScore * 100).toFixed(1) + ' %'}
            icon={<ExploitMaturityIconComponent maturity={maturity || cveDetails?.exploit?.maturity} />}
            caption={maturity || cveDetails?.exploit?.maturity}
          />
          {location.pathname !== '/' && (
            <NRMetricCard
              maxWidth="54%"
              metric={'Component'}
              result={name}
              icon={<NRSBOMIcon height={theme.spacing(4)} width={theme.spacing(4)} />}
              onCardClick={() => handleOnComponentClick(name, version, vendor)}
              caption={version}
              ariaLabel={`Open component details, ${name}, version ${version}${vendor ? `, ${vendor}` : ''}`}
            />
          )}
        </Stack>
        {location.pathname !== '/' && (
          <NRMetricBanner
            metric={'Status'}
            result={
              <Box sx={{ display: 'flex' }}>
                {REMEDIATION_STATUS_LABEL}
                <RemediationDetailsFormatter row={vulnerabilityData} placement="right" />
              </Box>
            }
            icon={<NRRemediateIcon width={theme.spacing(4)} height={theme.spacing(4)} color={REMEDIATION_STATUS_COLOR} />}
            rightIcon={
              <WithPermissions
                permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}
                rbacPermission={RBAC_PERMISSIONS.FINDINGS_REMEDIATE}
                resourceType={ACR_RESOURCE_TYPE.ASSET}
              >
                <RemediationButtonDrawerContainer row={vulnerabilityData} mr={0} />
              </WithPermissions>
            }
          />
        )}
        {location.pathname !== '/' && (
          <NRVulnerabilityJiraTickets advisoryId={advisoryId} identificationIds={identificationIds} />
        )}
      </Box>
      <Box id={scrollableId}>
        {!isLoading && (
          <Box id={scrollableId} sx={{ padding: theme.spacing(1.5, 0, 1.5) }}>
            <Typography tabIndex={0} mb={1} variant="subtitle1" children={DESCRIPTION} />
            <Typography tabIndex={0} color={'text.secondary'} mb={3} children={cveDetails.description} variant={'body2'} />
            <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: theme.spacing(3) }}>
              <NRKeyValueDataDisplay
                dataKey={NVD_STATUS}
                value={NVD_STATUS_DATA[cveDetails.nvdStatus].TITLE}
                endAdornment={
                  <Tooltip tabIndex={0} title={NVD_STATUS_DATA[cveDetails.nvdStatus].TOOLTIP}>
                    <InfoOutlined sx={{ color: theme.palette.text.iconInfo, width: theme.spacing(2), height: theme.spacing(2) }} />
                  </Tooltip>
                }
                setIconLeft
                keyWidth={'30%'}
                valueMaxWidth={'200px'}
              />
              <NRKeyValueDataDisplay
                dataKey={PUBLISHED_DATE}
                value={moment(cveDetails.createdDatetime).format(MONTH_DAY_YEAR_SHORT)}
                keyWidth={'30%'}
              />
              <NRKeyValueDataDisplay
                dataKey={MODIFIED_DATE}
                value={moment(cveDetails.processedDatetime).format(MONTH_DAY_YEAR_SHORT)}
                keyWidth={'30%'}
              />
              <NRKeyValueDataDisplay
                dataKey={CVE_SOURCE}
                value={<NRLink tooltipTitle={LINK_TOOLTIP} url={cveDetailURL} />}
                keyWidth={'30%'}
                valueMaxWidth="100%"
              />
            </Box>
            <Divider sx={{ marginTop: theme.spacing(3) }} />
          </Box>
        )}
        {isReachable && (
          <Box id={'NRVulnerabilityReachability'} tabIndex={-1} sx={{ padding: theme.spacing(1.5, 0, 1.5) }}>
            <Typography mb={1} variant="subtitle1" children={REACHABILITY_EVIDENCE} />
            <Box>
              <NRReachabilityContainer
                advisoryId={advisoryId}
                identificationIds={identificationIds}
                componentName={`${vulnerabilityData.name} ${vulnerabilityData.version}`}
                severityColor={SEVERITY_COLOR}
              />
            </Box>
            <Divider sx={{ marginTop: theme.spacing(3) }} />
          </Box>
        )}
      </Box>
    </>
  );
};
export default NRVulnerabilityDescription;
